import { useCallback, type FC, type ReactNode } from "react";
import {
  Alert,
  Input as AntInput,
  Checkbox,
  type CheckboxChangeEvent,
} from "antd";
import { InfoCircleOutlined, SelectOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { SeasonTaskSubmissionMode } from "../../../../types/gamePass";
import { buildGamePassImageUrl } from "../../../../utils/gamePassImageUpload";
import { FileDropzone, Input } from "../../../shared";
import SubmissionModeField from "../../shared/SubmissionModeField";

import {
  PREMIUM_TIER_ENABLED,
  SEASON_DESCRIPTION_MAX,
  type SeasonOverviewValues,
  type SeasonTiersValue,
} from "../createSeasonData";

import styles from "./SeasonOverview.module.css";

const { TextArea } = AntInput;

const CASINO_SUBMISSION_MODE: SeasonTaskSubmissionMode = "AUTO";

/** Visual contract for `SeasonOverview`.
 *
 *   wizard   — the Create Season Step 1 chrome.
 *   embedded — used by `EditSeasonModal`. The modal owns its own section
 *              headers; this component renders only the form controls.
 *   Both variants expose sportsbook/casino submission mode radios (FE-2080).
 *   Casino is locked to Auto; sportsbook Auto/Manual is selectable.
 */
export type SeasonOverviewVariant = "wizard" | "embedded";

export type SeasonOverviewProps = {
  values: SeasonOverviewValues;
  onValuesChange: (values: SeasonOverviewValues) => void;
  variant?: SeasonOverviewVariant;
  disabled?: boolean;
  testIdPrefix?: string;
};

const DEFAULT_TEST_ID_PREFIX = "create-season-overview";

const SeasonOverview: FC<SeasonOverviewProps> = ({
  values,
  onValuesChange,
  variant = "wizard",
  disabled = false,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
}) => {
  const update = useCallback(
    (patch: Partial<SeasonOverviewValues>) => {
      onValuesChange({ ...values, ...patch });
    },
    [onValuesChange, values],
  );

  const updateTier = useCallback(
    (patch: Partial<SeasonTiersValue>) => {
      onValuesChange({
        ...values,
        tiers: { ...values.tiers, ...patch },
      });
    },
    [onValuesChange, values],
  );

  const descriptionLength = values.description?.length ?? 0;
  const sportsbookSubmissionMode = values.sportsbookSubmissionMode ?? "AUTO";

  const isPremiumLocked = !PREMIUM_TIER_ENABLED && !values.tiers.premium;

  const containerTestId =
    variant === "embedded"
      ? `${testIdPrefix}-embedded`
      : "create-season-overview-form";

  const submissionFields: ReactNode = (
    <>
      <SubmissionModeField
        label={AppStrings.CREATE_SEASON_CASINO_TASK_SUBMISSION_LABEL}
        infoAria={AppStrings.CREATE_SEASON_CASINO_TASK_SUBMISSION_INFO_ARIA}
        testIdPrefix={testIdPrefix}
        fieldId="casino-submission"
        value={CASINO_SUBMISSION_MODE}
        disabled={disabled}
        manualDisabled
      />
      <SubmissionModeField
        label={AppStrings.CREATE_SEASON_SPORTSBOOK_TASK_SUBMISSION_LABEL}
        infoAria={
          AppStrings.CREATE_SEASON_SPORTSBOOK_TASK_SUBMISSION_INFO_ARIA
        }
        testIdPrefix={testIdPrefix}
        fieldId="sportsbook-submission"
        value={sportsbookSubmissionMode}
        disabled={disabled}
        onChange={(mode) => update({ sportsbookSubmissionMode: mode })}
      />
    </>
  );

  return (
    <div
      className={disabled ? `${styles.form} ${styles.formReadOnly}` : styles.form}
      data-testid={containerTestId}
      data-variant={variant}
    >
      {disabled && variant === "wizard" && (
        <Alert
          type="info"
          showIcon
          message={AppStrings.SEASON_OVERVIEW_DRAFT_LOCKED_INFO}
          className={styles.lockedInfo}
          data-testid={`${testIdPrefix}-locked-info`}
        />
      )}
      <section className={styles.section}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {`${AppStrings.SEASON_NAME}*`}
          </span>
          <Input
            value={values.name}
            placeholder={AppStrings.SEASON_NAME_PLACEHOLDER}
            disabled={disabled}
            onChange={(e) => update({ name: e.target.value })}
            data-testid={`${testIdPrefix}-name`}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {AppStrings.EDIT_SEASON_DESCRIPTION_LABEL}
          </span>
          <div className={styles.textareaWrapper}>
            <TextArea
              className="custom-form-input"
              value={values.description}
              placeholder={AppStrings.EDIT_SEASON_DESCRIPTION_PLACEHOLDER}
              autoSize={{ minRows: 3, maxRows: 5 }}
              maxLength={SEASON_DESCRIPTION_MAX}
              disabled={disabled}
              onChange={(e) => update({ description: e.target.value })}
              data-testid={`${testIdPrefix}-description`}
            />
            <span
              className={styles.charCount}
              aria-label={AppStrings.EDIT_SEASON_DESCRIPTION_COUNTER_ARIA}
              data-testid={`${testIdPrefix}-description-count`}
            >
              {`${descriptionLength}/${SEASON_DESCRIPTION_MAX}`}
            </span>
          </div>
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {AppStrings.GAME_PASS_SEASON_BANNER_LABEL}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              role="img"
              aria-label={AppStrings.GAME_PASS_SEASON_BANNER_INFO_ARIA}
              data-testid={`${testIdPrefix}-banner-info`}
            />
          </span>
          <FileDropzone
            value={values.imageFile ?? null}
            onChange={(file) =>
              update({ imageFile: file ?? undefined })
            }
            accept="image/*"
            initialUrl={
              values.imageUrl ?? buildGamePassImageUrl(values.imgKey)
            }
            onRemoveInitial={() => update({ imgKey: null, imageUrl: null })}
            icon={<SelectOutlined />}
            heading={AppStrings.GAME_PASS_STYLE_ASSETS_DRAG_AND_DROP}
            hint={AppStrings.GAME_PASS_SEASON_BANNER_HINT}
            buttonLabel={AppStrings.GAME_PASS_STYLE_ASSETS_UPLOAD}
            disabled={disabled}
            className={styles.bannerDropzone}
            data-testid={`${testIdPrefix}-banner-dropzone`}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {AppStrings.EDIT_SEASON_TIERS_LABEL}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              role="img"
              aria-label={AppStrings.EDIT_SEASON_TIERS_INFO_ARIA}
              data-testid={`${testIdPrefix}-tiers-info`}
            />
          </span>
          <div className={styles.tiersRow}>
            <label
              className={styles.tierLabel}
              data-testid={`${testIdPrefix}-tier-free-label`}
            >
              <Checkbox
                checked={values.tiers.free}
                disabled={disabled}
                onChange={(e: CheckboxChangeEvent) =>
                  updateTier({ free: e.target.checked })
                }
                data-testid={`${testIdPrefix}-tier-free`}
              />
              <span>{AppStrings.EDIT_SEASON_TIER_FREE}</span>
            </label>
            <label
              className={
                isPremiumLocked
                  ? `${styles.tierLabel} ${styles.tierLabelDisabled}`
                  : styles.tierLabel
              }
              data-testid={`${testIdPrefix}-tier-premium-label`}
            >
              <Checkbox
                checked={values.tiers.premium}
                disabled={disabled || isPremiumLocked}
                onChange={(e: CheckboxChangeEvent) =>
                  updateTier({ premium: e.target.checked })
                }
                data-testid={`${testIdPrefix}-tier-premium`}
              />
              <span>{AppStrings.EDIT_SEASON_TIER_PREMIUM}</span>
            </label>
          </div>
          {variant === "wizard" && values.tiers.premium && (
            <>
              <label
                className={styles.field}
                data-testid={`${testIdPrefix}-premium-price-gc-field`}
              >
                <span className={styles.fieldLabel}>
                  {`${AppStrings.EDIT_SEASON_PREMIUM_PRICE_GC_LABEL}*`}
                </span>
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={values.premiumPriceGc ?? ""}
                  placeholder={
                    AppStrings.EDIT_SEASON_PREMIUM_PRICE_GC_PLACEHOLDER
                  }
                  disabled={disabled}
                  onChange={(e) => update({ premiumPriceGc: e.target.value })}
                  data-testid={`${testIdPrefix}-premium-price-gc`}
                />
              </label>
              <label
                className={styles.field}
                data-testid={`${testIdPrefix}-premium-price-sc-field`}
              >
                <span className={styles.fieldLabel}>
                  {AppStrings.EDIT_SEASON_PREMIUM_PRICE_SC_LABEL}
                </span>
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={values.premiumPriceSc ?? ""}
                  placeholder={
                    AppStrings.EDIT_SEASON_PREMIUM_PRICE_SC_PLACEHOLDER
                  }
                  disabled={disabled}
                  onChange={(e) => update({ premiumPriceSc: e.target.value })}
                  data-testid={`${testIdPrefix}-premium-price-sc`}
                />
              </label>
            </>
          )}
        </div>

        {submissionFields}
      </section>
    </div>
  );
};

export default SeasonOverview;
